/**
 * Route guards.
 */
import type React                     from "react";
import { Navigate, useLocation }      from "react-router-dom";
import { useAuth }                    from "@features/auth/AuthContext";
import { useCan }                     from "@features/auth/hooks/useCan";
import { useModule, type ModuleName } from "@features/auth/hooks/useModule";
import type { PermissionKey }         from "@features/auth/permissions";
import type { AuthUser, ScopeLevel }  from "@features/auth/api/auth.schemas";

export const FullPageSpinner: React.FC = () => (
  <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg-primary)", color: "var(--text-muted)" }}>
    <i className="fas fa-circle-notch fa-spin" style={{ fontSize: "1.5rem" }} />
  </div>
);

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, initialising } = useAuth();
  const location = useLocation();
  if (initialising) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
};

export const RequirePerm: React.FC<{ perm: PermissionKey; children: React.ReactNode }> = ({ perm, children }) => {
  const { can } = useCan();
  const { user } = useAuth();
  if (!can(perm)) return <Navigate to={landingPathFor(user)} replace />;
  return <>{children}</>;
};

/**
 * Module guard (handoff §3, Variation A). If the org has this module disabled,
 * redirect home rather than showing a "module disabled" page — the sidebar
 * already hides the entry; this stops a typed/bookmarked URL from reaching the
 * page. Platform/group admins are `unrestricted`, so hasModule is always true.
 *
 * This is UX defence-in-depth. The real guarantee is the server's 403
 * module_disabled, handled by ModuleDisabledListener.
 */
export const RequireModule: React.FC<{ module: ModuleName; children: React.ReactNode }> = ({ module, children }) => {
  const { hasModule } = useModule();
  const { user } = useAuth();
  if (!hasModule(module)) return <Navigate to={landingPathFor(user)} replace />;
  return <>{children}</>;
};

const SCOPE_RANK: Record<ScopeLevel, number> = { platform: 3, group: 2, org: 1 };

/**
 * Minimum-scope guard: `scope` and anything broader may enter.
 * e.g. scope="group" admits tenant_admin and super_admin.
 */
export const RequireScope: React.FC<{ scope: ScopeLevel; children: React.ReactNode }> = ({ scope, children }) => {
  const { user } = useAuth();
  const userRank = SCOPE_RANK[user?.scope_level ?? "org"];
  const required = SCOPE_RANK[scope];
  if (userRank < required) return <Navigate to={landingPathFor(user)} replace />;
  return <>{children}</>;
};

/**
 * Exact-scope guard: ONLY this scope may enter.
 * Agent enrollment and FIM belong to an organization, so a platform admin has
 * no org context to act in — broader scope is not "more access" here.
 */
export const RequireExactScope: React.FC<{ scope: ScopeLevel; children: React.ReactNode }> = ({ scope, children }) => {
  const { user } = useAuth();
  if (user?.scope_level !== scope) return <Navigate to={landingPathFor(user)} replace />;
  return <>{children}</>;
};

export function landingPathFor(user: AuthUser | null): string {
  if (!user) return "/login";
  switch (user.role) {
    case "super_admin": return "/groups";
    case "tenant_admin": return "/organizations";
    case "org_admin":
    case "viewer":
      // Org-scoped users never see the group/org management screens.
      return "/endpoint-inventory";
    case "org_auditor":
      return "/endpoint-inventory";
    default: return "/login";
  }
}
